import { DateOnly } from '@if/core/date'
import {
  EBeneficiaryType,
  ECreditApplicationStatus,
  EDecision,
  EFinancingProgram,
  ELanguage,
  EProvince,
  getEProvinceList,
} from '@if/core/types'
import { Banner, PageContainer } from '@if/mui'
import { Skeleton, Stack } from '@mui/material'
import { useMerchant } from '@src/containers/App/MerchantProvider'
import { useSaveCreditApplicationDraft } from '@src/data/api/credit-api/credit-api'
import { useQuoteById } from '@src/data/api/quote-api/quote-api'
import { toELanguage } from '@src/data/language-selectors'
import { useProfileLanguage } from '@src/hooks/language-hooks'
import { CreditApplication, DraftCreditApplicationSchema } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { QuoteApplicationPrefill } from '@src/types/QuoteApplicationPrefill'
import { useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { adjustCreditApplicationYearsForApi } from '../ViewCreditApplicationPage/credit-hooks'
import ApplicationForm from './components/ApplicationForm'

const PROVINCES_BY_CODE = new Map<string, EProvince>(
  getEProvinceList().map((province) => [province.toUpperCase(), province]),
)

// Merges quote pre-fill data (#66104) over a fresh draft. Only fields with an
// unambiguous 1:1 match are applied — merchant/financingProgram/income/expenses stay
// driven by the route and the dealer's own entry, never by the quote.
//
// The quote's address is copied field by field rather than spread: the API returns the whole
// Address shape with nulls for what BrokerGateway never fills (country, civicNumber, apartment,
// suite), and spreading those over the draft wipes schema defaults — country would reach the
// API as null instead of 'Canada'.
//
// The quote never supplies a civicNumber, so a prefilled address trips AddressSchema's
// conditional civicNumber requirement and the dealer sees it as a field to complete —
// expected: the dealer reviews the form before submitting.
const buildInitialCreditApplication = (
  quote: QuoteApplicationPrefill | null,
  languageId: ELanguage,
): CreditApplication => {
  const base = DraftCreditApplicationSchema().getDefault() as unknown as CreditApplication
  // The schema defaults the language to whatever the dashboard displays; the account's own choice wins.
  const draft = { ...base, applicant: { ...base.applicant, languageId } }
  if (!quote) return draft

  // The quote's province string isn't guaranteed to match an EProvince member exactly; an
  // unvalidated value could silently defeat province-specific logic (e.g. Quebec consent)
  // before the form's own validation runs. Matched case-insensitively.
  const stateIso = PROVINCES_BY_CODE.get(quote.currentAddress.stateIso.toUpperCase()) ?? null

  return {
    ...draft,
    requestedLoanAmount: quote.requestedLoanAmount,
    applicant: {
      ...draft.applicant,
      firstName: quote.firstName,
      lastName: quote.lastName,
      birthDate: new DateOnly(quote.birthDate),
      cellPhone: quote.cellPhone,
      email: quote.email,
      currentAddress: {
        ...draft.applicant.currentAddress,
        street: quote.currentAddress.street,
        city: quote.currentAddress.city,
        postalCode: quote.currentAddress.postalCode,
        civicNumber: quote.currentAddress.civicNumber,
        stateIso,
      },
    },
  }
}

interface CreateCreditApplicationFormProps {
  merchant: Merchant
  financingProgramId: EFinancingProgram
  quote: QuoteApplicationPrefill | null
  // Id of the quote `quote` came from — sent on create so the backend can attribute the
  // quote's broker to the new application (#68811). Null when no quote pre-filled the form.
  quoteId: string | null
}

// Mounted only once the quote fetch (if any) has resolved, so its useState initializer
// always sees the final quote value instead of the in-flight `null`.
const CreateCreditApplicationForm = ({
  merchant,
  financingProgramId,
  quote,
  quoteId,
}: CreateCreditApplicationFormProps): JSX.Element => {
  const [saveCreditAppMutation, , , reset] = useSaveCreditApplicationDraft(quoteId)
  const profileLanguage = useProfileLanguage()
  const [creditApplication] = useState<CreditApplication>(() =>
    buildInitialCreditApplication(quote, toELanguage(profileLanguage)),
  )
  const navigate = useNavigate()

  const onSave = async (data: Partial<CreditApplication>) => {
    if (!data.beneficiaryTypeId) data.beneficiaryTypeId = EBeneficiaryType.Applicant
    adjustCreditApplicationYearsForApi(data)
    await saveCreditAppMutation(data).then((creditApp) => {
      reset()
      if (creditApp.status === ECreditApplicationStatus.Draft) navigate('/credits')
      else {
        navigate(`/credits/${creditApp.financingProgramId}/${creditApp.id}`)
      }
    })
  }
  const isPrequalComplete =
    creditApplication?.prequalificationDecision?.decision === EDecision.Approved ||
    (creditApplication?.prequalificationDecision?.decision === EDecision.Refused &&
      creditApplication?.applicant?.hasCreditReport === true)

  return (
    <ApplicationForm
      mode="create"
      merchant={merchant}
      creditApplication={creditApplication}
      onSave={onSave}
      financingProgramId={financingProgramId}
      isPrequalComplete={isPrequalComplete}
      isApplicantSinNeeded={false}
      isCoapplicantSinNeeded={false}
    />
  )
}

const CreateCreditApplicationPage = (): JSX.Element => {
  const { t } = useTranslation()
  const { financingProgramId } = useParams<{ financingProgramId: EFinancingProgram }>()
  const merchant = useMerchant()

  const [searchParams] = useSearchParams()
  const quoteId = searchParams.get('quoteId')
  const [quote, isFetchingQuote, isQuoteError] = useQuoteById(quoteId)

  return (
    <PageContainer title={t('newApplication.title')} subtitle={t('newApplication.subtitle')}>
      {isQuoteError && (
        <Banner severity="warning" title={t('common.error')} sx={{ mb: 2 }}>
          {t('common.errors.quotePrefillFailed')}
        </Banner>
      )}
      {isFetchingQuote ? (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="rounded" height={220} />
          <Skeleton variant="rounded" height={120} />
        </Stack>
      ) : (
        <CreateCreditApplicationForm
          merchant={merchant}
          financingProgramId={financingProgramId!}
          quote={quote}
          // Only the quote that actually pre-filled the form may bring its broker over: a
          // failed quote fetch leaves the form empty, so no broker is attributed.
          quoteId={quote ? quoteId : null}
        />
      )}
    </PageContainer>
  )
}

export default CreateCreditApplicationPage
